import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { Search, Brain, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

export default function Scanner() {
  const { t } = useLang();
  const [tokens, setTokens] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [signals, setSignals] = useState({});
  const [signalLoad, setSignalLoad] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get(`/market/pumpfun?limit=30`);
      setTokens(data || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  const search = async (e) => {
    e?.preventDefault?.();
    if (!q) { load(); return; }
    setLoading(true);
    try {
      const { data } = await client.get(`/market/search?q=${encodeURIComponent(q)}&chain=solana`);
      setTokens(data || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const generate = async (tok) => {
    setSignalLoad(tok.address);
    try {
      const { data } = await client.post("/ai/signal", { token_symbol: tok.symbol, token_address: tok.address, chain: tok.chain });
      setSignals(s => ({ ...s, [tok.address]: data }));
      toast.success(`AI: ${data.action}`);
    } catch (e) { toast.error("AI failed"); } finally { setSignalLoad(null); }
  };

  const tier = (v) => { const l = t(`tier_${v}`); return l === `tier_${v}` ? null : l; };

  return (
    <div data-testid="scanner-page" className="max-w-[1400px] mx-auto px-6 py-10">
      <div className="text-[10px] uppercase tracking-widest text-[#CCFF00] font-mono">Scanner</div>
      <h1 className="font-display text-3xl md:text-4xl font-bold mt-1">AI Token Scanner</h1>

      <form onSubmit={search} className="mt-6 flex gap-3 max-w-xl">
        <div className="flex-1 flex items-center border border-[#222] focus-within:border-[#CCFF00]">
          <Search size={14} className="ml-3 text-white/40" />
          <input data-testid="scanner-search-input" value={q} onChange={(e)=>setQ(e.target.value)} placeholder={t("search")}
            className="flex-1 bg-transparent focus:outline-none px-3 py-3 font-mono text-sm" />
        </div>
        <button data-testid="scanner-search-button" className="btn-acid !py-3 !px-5 !text-xs">{loading ? "…" : "Search"}</button>
      </form>

      <div className="mt-8 surface overflow-hidden">
        <div className="grid grid-cols-12 px-4 py-3 border-b border-[#222] text-[10px] uppercase tracking-widest text-white/40 font-mono">
          <div className="col-span-3">Token</div>
          <div className="col-span-2 text-right">{t("price")}</div>
          <div className="col-span-2 text-right">{t("market_cap")}</div>
          <div className="col-span-1 text-right">{t("age")}</div>
          <div className="col-span-1 text-right">{t("score")}</div>
          <div className="col-span-1 text-right">{t("multiplier")}</div>
          <div className="col-span-2 text-right">Action</div>
        </div>
        {loading && <div className="p-8 text-center text-white/40 font-mono"><Loader2 className="inline animate-spin" size={16} /></div>}
        {!loading && tokens.length === 0 && <div className="p-8 text-center text-white/40 text-sm">No tokens</div>}
        {tokens.map(t => (
          <div key={t.address || t.symbol} className="border-b border-[#222] last:border-0">
            <div className="grid grid-cols-12 px-4 py-3 items-center hover:bg-[#141414]">
              <div className="col-span-3">
                <div className="font-display text-sm font-semibold flex items-center gap-2">
                  {t.symbol}
                  {t.source === "pumpfun" && <span className="text-[9px] bg-[#FF3366]/20 text-[#FF3366] px-1.5 py-0.5 font-mono">PUMP</span>}
                </div>
                <div className="text-[10px] text-white/40 truncate">{t.name}</div>
              </div>
              <div className="col-span-2 text-right font-mono text-xs">${Number(t.price_usd).toFixed(8)}</div>
              <div className="col-span-2 text-right font-mono text-xs text-white/60">${Math.round(t.market_cap).toLocaleString()}</div>
              <div className="col-span-1 text-right font-mono text-xs text-white/60">{Number(t.age_hours).toFixed(1)}h</div>
              <div className="col-span-1 text-right font-mono text-xs text-[#CCFF00]">{Number(t.score).toFixed(1)}</div>
              <div className="col-span-1 text-right"><span className="inline-block px-2 py-1 bg-[#CCFF00]/10 text-[#CCFF00] text-[9px] font-mono">{tier(t.multiplier_potential)}</span></div>
              <div className="col-span-2 flex justify-end gap-1">
                <button data-testid={`scanner-ai-${t.symbol}`} onClick={() => generate(t)} className="px-2 py-1.5 border border-[#222] hover:border-[#CCFF00] text-[10px] font-mono flex items-center gap-1">
                  {signalLoad === t.address ? <Loader2 size={10} className="animate-spin" /> : <Brain size={10} />} AI
                </button>
                {t.dex_url && <a href={t.dex_url} target="_blank" rel="noreferrer" className="px-2 py-1.5 border border-[#222] hover:border-[#CCFF00]"><ExternalLink size={10} /></a>}
              </div>
            </div>
            {signals[t.address] && (
              <div className="px-4 py-3 bg-[#0A0A0A] border-t border-[#222]">
                <div className="flex items-center gap-3 text-xs">
                  <span className={`font-mono font-bold ${signals[t.address].action === "BUY" ? "text-[#CCFF00]" : signals[t.address].action === "SELL" ? "text-[#FF3366]" : "text-white/60"}`}>
                    {signals[t.address].action}
                  </span>
                  <span className="text-white/40 font-mono">conf {(signals[t.address].confidence * 100).toFixed(0)}%</span>
                  <span className="text-white/40 font-mono">risk {signals[t.address].risk_level}</span>
                  {tier(signals[t.address].target_multiplier) && <span className="text-white/40 font-mono">target {tier(signals[t.address].target_multiplier)}</span>}
                </div>
                <div className="mt-2 text-[11px] text-white/60">{signals[t.address].reasoning}</div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
